import { Store, Package, ShoppingBag, Camera, Clock } from 'lucide-react';
import { HealthScoreCircle } from './HealthBadge';

interface CountryCardProps {
  code: string;
  name: string;
  storeCount: number;
  productCount: number;
  lastChanceCount: number;
  physicalCount: number;
  healthScore: number;
  healthStatus: 'healthy' | 'warning' | 'error';
  lastUpdated: string | null;
  metrics: {
    completeness: number;
    freshness_24h: number;
    freshness_7d: number;
  };
  onClick: () => void;
}

// Country flags using emoji (works across all platforms)
const countryFlags: Record<string, string> = {
  US: '🇺🇸', CA: '🇨🇦', MX: '🇲🇽', DE: '🇩🇪', GB: '🇬🇧', FR: '🇫🇷',
  SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮', NL: '🇳🇱', BE: '🇧🇪',
  AT: '🇦🇹', CH: '🇨🇭', ES: '🇪🇸', IT: '🇮🇹', PL: '🇵🇱', IE: '🇮🇪',
  PT: '🇵🇹', CZ: '🇨🇿', HU: '🇭🇺', RO: '🇷🇴', SK: '🇸🇰', GR: '🇬🇷',
  SI: '🇸🇮', HR: '🇭🇷', RS: '🇷🇸', JP: '🇯🇵', KR: '🇰🇷', CN: '🇨🇳',
  TW: '🇹🇼', HK: '🇭🇰', SG: '🇸🇬', MY: '🇲🇾', TH: '🇹🇭', ID: '🇮🇩',
  PH: '🇵🇭', IN: '🇮🇳', AU: '🇦🇺', AE: '🇦🇪', SA: '🇸🇦', QA: '🇶🇦',
  KW: '🇰🇼', BH: '🇧🇭', JO: '🇯🇴', IL: '🇮🇱', EG: '🇪🇬', MA: '🇲🇦',
  DO: '🇩🇴', PR: '🇵🇷', CL: '🇨🇱',
};

function formatTimeAgo(dateString: string | null): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

export function CountryCard({
  code,
  name,
  storeCount,
  productCount,
  lastChanceCount,
  physicalCount,
  healthScore,
  // healthStatus is passed but not used currently - available for future use
  lastUpdated,
  metrics,
  onClick,
}: CountryCardProps) {
  const flag = countryFlags[code] || '🏳️';

  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer hover:border-primary-300 hover:shadow-md transition-all text-left w-full"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{flag}</span>
          <div>
            <h3 className="font-semibold text-gray-900">{name}</h3>
            <p className="text-sm text-gray-500">{code}</p>
          </div>
        </div>
        <HealthScoreCircle score={healthScore} size={48} strokeWidth={4} />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Store size={14} className="text-gray-400" />
          <span className="text-gray-600">{storeCount || 0} stores</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Package size={14} className="text-blue-500" />
          <span className="text-gray-600">{(productCount || 0).toLocaleString()} As-Is</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <ShoppingBag size={14} className="text-orange-500" />
          <span className="text-gray-600">{(lastChanceCount || 0).toLocaleString()} Last Chance</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Camera size={14} className="text-purple-500" />
          <span className="text-gray-600">{(physicalCount || 0).toLocaleString()} Physical</span>
        </div>
      </div>

      {/* Metrics Bars */}
      <div className="space-y-2 mb-4">
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-500">Completeness</span>
            <span className="font-medium">{metrics?.completeness ?? 0}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${metrics?.completeness ?? 0}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-gray-500">Fresh (24h)</span>
            <span className="font-medium">{metrics?.freshness_24h ?? 0}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full"
              style={{ width: `${metrics?.freshness_24h ?? 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Last Updated */}
      <div className="flex items-center gap-1 text-xs text-gray-400">
        <Clock size={12} />
        <span>Updated {formatTimeAgo(lastUpdated)}</span>
      </div>
    </button>
  );
}
